import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useEffect, useState } from 'react'
import { calculateMinMaxPorts } from 'app/plugins/openstack/components/networks/security-groups/helpers'
import { portRangeValidator } from 'app/plugins/openstack/components/networks/security-groups/SecurityGroupRulesParamFields'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import DropdownField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Card from 'core/elements/card'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import useFieldsArrayManager from 'core/hooks/useFieldsArrayManager'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { nameValidator, validIpv4Validator } from 'core/utils/fieldValidators'
import ProtocolPicklist from 'k8s/components/clusters-dashboard/ProtocolPicklist'
import {
  createKaapiClusterAddon,
  deleteKaapiClusterAddon,
  updateKaapiClusterAddon,
} from 'k8s/components/kaapi/cluster-addons/actions'
import { noop } from 'utils/fp'
import { METALLB_VERSION } from '../cluster/deployment/helper'
import { createKaapiClusterAddonBody } from '../kaapi/cluster-addons/action-helpers'

const defaultAllowedAddresses = { ipAddress: '' }
const defaultPortDetails = { name: '', portRange: '', protocol: '', remoteAddressPool: '' }

const defaultParams = {
  addressPools: [],
  portDetails: [],
  isMetalLBEnabled: false,
}

const createOptionalAddons = async (clusterName, namespace, createClusterAddon) => {
  const body = createKaapiClusterAddonBody({
    clusterName,
    namespace,
    addonType: 'metallb',
    version: METALLB_VERSION,
  })
  return createClusterAddon({ namespace, body })
}

const getMetallbConfigBody = (params) => {
  return {
    spec: {
      clusterSpec: {
        firewallConfig: {
          allowedAddressPools: params?.addressPools?.map((addr) => addr?.ipAddress) || [],
          inboundPorts:
            params?.portDetails?.map((port) => {
              const { portMinRange, portMaxRange } = calculateMinMaxPorts(port?.portRange || '')
              return {
                portMin: portMinRange,
                portMax: portMaxRange,
                name: port?.name,
                protocol: port?.protocol,
                remoteAddressPool: port?.remoteAddressPool,
              }
            }) || [],
        },
      },
    },
  }
}
const ConfigureMetalLBModal = ({ onClose, rows: clusters, reload }) => {
  const classes = useStyles()
  const [addonError, setAddonError] = useState(null)
  const { params, getParamsUpdater, updateParams } = useParams(defaultParams)

  const {
    update: createClusterAddon,
    updating: creatingClusterAddon,
    error: createClusterAddonError,
    reset: resetCreateClusterAddon,
  } = useUpdateAction(createKaapiClusterAddon)

  const {
    update: deleteClusterAddon,
    updating: deletingClusterAddon,
    error: deleteClusterAddonError,
    reset: resetDeleteClusterAddon,
  } = useUpdateAction(deleteKaapiClusterAddon)

  const { update: updateAddonFn, updating, error, reset } = useUpdateAction(updateKaapiClusterAddon)

  const cluster = clusters && clusters[0]

  useEffect(() => {
    if (cluster) {
      const { allowedAddressPools, inboundPorts } =
        cluster?.metalLBAddon?.spec?.clusterSpec?.firewallConfig || {}
      updateParams({
        isMetalLBEnabled: cluster?.isMetalLBAddon,
        addressPools: allowedAddressPools?.map((addr) => ({ ipAddress: addr })) || [],
        portDetails:
          inboundPorts?.map((port) => ({
            name: port?.name,
            portRange: `${port?.portMin}-${port?.portMax}`,
            protocol: port?.protocol,
            remoteAddressPool: port?.remoteAddressPool,
          })) || [],
      })
    }
  }, [cluster])

  const allowedAddressesOps = useFieldsArrayManager(
    params.addressPools,
    (arr) => updateParams({ addressPools: arr }),
    defaultAllowedAddresses,
  )
  const portDetailsOps = useFieldsArrayManager(
    params.portDetails,
    (arr) => updateParams({ portDetails: arr }),
    defaultPortDetails,
  )

  const handleSubmit = useCallback(async () => {
    setAddonError(null)

    if (!params.isMetalLBEnabled && cluster?.isMetalLBAddon === true) {
      const { success, error } = await deleteClusterAddon({
        name: `metallb-addon-${cluster?.metadata?.name}`,
        namespace: cluster?.metadata?.namespace,
      })
      if (success) {
        reload()
        resetDeleteClusterAddon()
        onClose()
        return
      }

      setAddonError({
        title: 'Error in disabling the addon',
        message: error || 'Something went wrong',
      })
      return
    }

    if (cluster?.isMetalLBAddon !== true) {
      const { success: addonSuccess, error: createAddonError } = await createOptionalAddons(
        cluster?.metadata?.name || '',
        cluster?.metadata?.namespace,
        createClusterAddon,
      )
      if (!addonSuccess) {
        setAddonError(createAddonError)
        return
      }
    }

    const body = getMetallbConfigBody(params)
    const { success, error } = await updateAddonFn({
      namespace: cluster?.metadata?.namespace,
      customBody: body,
      name: `metallb-addon-${cluster?.metadata?.name}`,
    })
    if (success) {
      reset()
      resetCreateClusterAddon()
      reload()
      onClose()
      return
    }
    setAddonError(error)
  }, [
    cluster,
    params,
    setAddonError,
    deleteClusterAddon,
    createClusterAddon,
    updateAddonFn,
    reset,
    resetDeleteClusterAddon,
    resetCreateClusterAddon,
    reload,
    onClose,
  ])

  return (
    <ModalForm
      open
      onClose={onClose}
      submitTitle="Submit"
      title="Expose Load Balancer services"
      onSubmit={handleSubmit}
      submitting={updating || creatingClusterAddon || deletingClusterAddon}
      error={error || createClusterAddonError || addonError || deleteClusterAddonError}
    >
      <FormFieldSection
        title={!params.isMetalLBEnabled ? 'Enable MetalLB' : 'Disable MetalLB'}
        info="Enabling MetalLB will configure load balancer support for your cluster. Disabling it will remove the addon and permanently delete all associated configurations."
      >
        <ToggleSwitchField
          id="enableDisableMetalLb"
          value={params.isMetalLBEnabled}
          onChange={getParamsUpdater('isMetalLBEnabled')}
          label="MetalLB"
        />
      </FormFieldSection>
      <FormFieldSection
        title="Allowed Addresses"
        info="Enter Load Balancer IPs, (e.g from MetalLB IP address pool)"
      >
        {params?.addressPools?.map((addressPool, idx) => (
          <Tooltip
            align={{ vertical: 'middle', horizontal: 'left' }}
            message={
              !params.isMetalLBEnabled ? 'To edit address pairs you need to enable MetalLB' : ''
            }
            key={idx}
          >
            <div className={classes.addressFieldContainer}>
              <TextField
                id={`allowedAddressPairs.${idx}.ipAddress`}
                label="IP Address or CIDR"
                onChange={(value) => {
                  allowedAddressesOps?.updateAt({
                    idx,
                    field: addressPool,
                    values: { ipAddress: value },
                  })
                }}
                value={addressPool?.ipAddress}
                placeholder="eg. 12.12.11.12"
                required
                disabled={!params.isMetalLBEnabled}
                validations={[validIpv4Validator]}
              />

              <div
                className={classes.removePair}
                onClick={() =>
                  params.isMetalLBEnabled ? allowedAddressesOps?.removeAt(idx) : noop()
                }
              >
                <FontAwesomeIcon className={classes.icon} size="lg" solid>
                  circle-minus
                </FontAwesomeIcon>
              </div>
            </div>
          </Tooltip>
        ))}
        <Tooltip
          align={{ vertical: 'middle', horizontal: 'left' }}
          message={
            !params.isMetalLBEnabled ? 'To add address pairs you need to enable MetalLB' : ''
          }
        >
          <div
            className={classes.operationLabel}
            onClick={params.isMetalLBEnabled ? allowedAddressesOps.add : noop}
          >
            <FontAwesomeIcon className={classes.icon} size="lg" solid>
              circle-plus
            </FontAwesomeIcon>
            <Text variant="body2">Add Address</Text>
          </div>
        </Tooltip>
      </FormFieldSection>
      <FormFieldSection title="Inbound Port Details">
        {params?.portDetails?.map((port, idx) => (
          <Tooltip
            align={{ vertical: 'middle', horizontal: 'left' }}
            message={
              !params.isMetalLBEnabled ? 'To edit port details you need to enable MetalLB' : ''
            }
            key={idx}
          >
            <Card className={classes.portFieldCard}>
              <div className={classes.portFieldContainer}>
                <TextField
                  id={`portDetails.${idx}.name`}
                  label="Name"
                  onChange={(value) => {
                    portDetailsOps.updateAt({ idx, field: port, values: { name: value } })
                  }}
                  value={port?.name}
                  info="The port name should start with a lowercase letter and can contain letters, numbers and '-'"
                  required
                  placeholder="e.g. port-80"
                  disabled={!params.isMetalLBEnabled}
                  validations={[nameValidator('Load Balancer Port name is invalid')]}
                />
                <TextField
                  id={`portDetails.${idx}.portRange`}
                  label="Port"
                  onChange={(value) => {
                    portDetailsOps?.updateAt({ idx, field: port, values: { portRange: value } })
                  }}
                  value={port?.portRange}
                  placeholder="e.g. 80 or 1 - 65535"
                  disabled={!params.isMetalLBEnabled}
                  required
                  validations={[portRangeValidator]}
                />
                <DropdownField
                  DropdownComponent={ProtocolPicklist}
                  name={`portDetails.${idx}.protocol`}
                  id={`portDetails.${idx}.protocol`}
                  label="Protocol"
                  disabled={!params.isMetalLBEnabled}
                  compact={false}
                  onChange={(value) => {
                    portDetailsOps?.updateAt({ idx, field: port, values: { protocol: value } })
                  }}
                  value={port?.protocol}
                  required
                />

                <TextField
                  id={`portDetails.${idx}.remoteAddressPool`}
                  label="IP Address or CIDR"
                  onChange={(value) => {
                    portDetailsOps?.updateAt({
                      idx,
                      field: port,
                      values: { remoteAddressPool: value },
                    })
                  }}
                  value={port?.remoteAddressPool}
                  placeholder="eg. 12.12.11.12"
                  disabled={!params.isMetalLBEnabled}
                  required
                  validations={[validIpv4Validator]}
                  info="Enter the source IPs from which incoming traffic is allowed."
                />

                <div
                  className={classes.removePortField}
                  onClick={() => (params.isMetalLBEnabled ? portDetailsOps?.removeAt(idx) : noop())}
                >
                  <FontAwesomeIcon className={classes.icon} size="lg" solid>
                    circle-minus
                  </FontAwesomeIcon>
                  <Text variant="body2">Remove Inbound Port Detail</Text>
                </div>
              </div>
            </Card>
          </Tooltip>
        ))}
        <Tooltip
          message={!params.isMetalLBEnabled ? 'To add port details you need to enable MetalLB' : ''}
          align={{ vertical: 'middle', horizontal: 'left' }}
        >
          <div
            className={classes.operationLabel}
            onClick={params.isMetalLBEnabled ? portDetailsOps.add : noop}
          >
            <FontAwesomeIcon className={classes.icon} size="lg" solid>
              circle-plus
            </FontAwesomeIcon>
            <Text variant="body2">Add Inbound Port Detail</Text>
          </div>
        </Tooltip>
      </FormFieldSection>
    </ModalForm>
  )
}

export default ConfigureMetalLBModal

const useStyles = makeStyles<Theme>((theme) => ({
  operationLabel: {
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: theme.spacing(1),
  },
  removePair: {
    marginTop: theme.spacing(4.5),
    cursor: 'pointer',
  },
  icon: {
    color: theme.components.badge.primary.color,
  },
  addressFieldContainer: {
    display: 'flex',
    gap: 16,
  },
  portFieldContainer: {
    display: 'grid',
    gap: 16,
    maxWidth: '400px',
  },
  removePortField: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  portFieldCard: {
    maxWidth: '400px',
  },
}))
